import React from 'react';
import { Link } from 'react-router';
import { useGetTradeSuggestionsQuery } from 'store/services/tradeSuggestions/tradeSuggestionsApi';
import { useGetPrivacyPreferencesQuery } from 'store/services/settings/settingsApi';
import SuggestionDeck from './components/SuggestionDeck';
import {
  SuggestionsEmptyAllHandled,
  SuggestionsEmptyNoMatches,
} from './components/SuggestionsEmptyStates';
import './TradeSuggestionsPage.css';

/**
 * A single page fetch. The backend caps a user at 10 ACTIVE suggestions, so the
 * whole set fits in one request and there is no pager: the deck IS the
 * navigation. `limit` is server-clamped to 50, so asking for it is safe.
 */
const PAGE_LIMIT = 50;

/**
 * The Suggested tab body: one complete, ready-to-propose trade at a time with
 * the rest of the hand behind it, a running countdown, a pass flow with an undo
 * window, and a CTA that validates before seeding the builder.
 *
 * This was TradeSuggestionsPage, the standalone /trade-suggestions surface. It
 * keeps its stylesheet and its whole component tree; only the page furniture
 * moved out — the <h1> and the SEO tags now belong to the Offers page, and
 * /trade-suggestions redirects here.
 */
export default function SuggestedTab() {
  const { data, isLoading, error } = useGetTradeSuggestionsQuery({ page: 1, limit: PAGE_LIMIT });
  // The engine excludes opted-out users in BOTH roles, so this tab goes empty
  // as a direct result of the user's own setting. Fail open, as everywhere else:
  // only an explicit `false` claims that is the reason.
  const { data: privacy } = useGetPrivacyPreferencesQuery();
  const offersDisabled = privacy?.allowIncomingTradeOffers === false;

  return (
    <div className="sugg-page sugg-page--tab" role="tabpanel" aria-label="Suggested trades">
      {isLoading && (
        <div className="sugg-page-loading">
          <span className="loading-spinner-small" />
          <p>Finding trades for you…</p>
        </div>
      )}

      {!isLoading && (error || !data) && (
        <div className="sugg-page-error">
          Suggestions aren&apos;t available right now. Check back soon.
        </div>
      )}

      {data && data.suggestions.length === 0 && (
        <div className="sugg-page-empty">
          {/* A state the user caused must not present as an unexplained absence:
              the stock empty states tell them to widen their lists, which would
              not help and reads as the feature being broken. Named first, and
              linked to the setting that undoes it. */}
          {offersDisabled ? (
            <p className="sugg-page-empty-reason">
              You have trade offers turned off, so we aren&apos;t suggesting trades for you.{' '}
              <Link to="/settings/privacy">Turn them back on</Link> to start seeing suggestions
              again.
            </p>
          ) : data.emptyReason === 'all_handled' ? (
            <SuggestionsEmptyAllHandled variant="page" />
          ) : (
            <SuggestionsEmptyNoMatches variant="page" />
          )}
        </div>
      )}

      {/* The deck owns the help block: it must disappear along with the card
          when the hand runs out, which only the deck knows. */}
      {data && data.suggestions.length > 0 && <SuggestionDeck suggestions={data.suggestions} />}
    </div>
  );
}
